<script setup lang="ts">
import {
  getTranslatedProperty,
  getCategoryRoute,
} from "@shopware-pwa/helpers-next";

const { navigationElements } = useNavigation({ type: "footer-navigation" });
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const { getPaymentMethods, getShippingMethods } = useCheckout();

const payments = await getPaymentMethods();
const shippingMethods = await getShippingMethods();

const showButton = ref(false);

const handleScroll = () => {
  showButton.value = window.scrollY > 200; 
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};
</script>

<template>
  <div class="footer-element">
    <LayoutCustomFooter />

    <footer class="bg-dark-bg py-10 px-4">
      <!--grid grid-cols-2 md:grid-cols-2 lg:grid-cols-7 gap-8-->
      <div
        class="container mx-auto flex justify-between flex-wrap xl:flex-nowrap"
      >
        <div class="about-us w-1/2 md:w-auto">
          <h6 class="text-white text-sm font-bold leading-6 mb-2">
            {{ $t("footer.headline.companyName") }}
          </h6>
          <ul class="space-y-1">
            <li class="text-light-grey text-xs leading-4">
              {{ $t("footer.companyDetails.adderss1") }}
            </li>
            <li class="text-light-grey text-xs leading-4">
              {{ $t("footer.companyDetails.address2") }}
            </li>
            <li class="text-light-grey text-xs leading-4">
              <a href="tel:+41326173260" class="text-light-grey text-xs"
                >+41 32 617 32 60
              </a>
            </li>
            <li class="text-light-grey text-xs leading-4">
              <a href="mailto:kisag@kisag.ch" class="text-light-grey text-xs"
                >kisag@kisag.ch</a
              >
            </li>
          </ul>
        </div>

        <!--:class="navigationElement.customFields?.custom_menu_class"-->
        <div
          class="information w-1/2 md:w-auto"
          v-for="navigationElement in navigationElements"
          :key="navigationElement.id"
          :class="navigationElement.customFields?.custom_menu_class ? 'w-full md:w-1/2 xl:w-auto mt-7 md:mt-0' : ''"
        >
          <h6 class="text-white text-sm font-bold leading-6 mb-2">
            {{ getTranslatedProperty(navigationElement, "name") }}
          </h6>
          <template v-if="navigationElement.childCount > 0">
            <ul
              class="space-y-1"
              :class="{
                'gap-0 md:gap-12':
                  navigationElement.customFields?.custom_menu_grid,
              }"
            >
              <li
                v-for="navigationChild in navigationElement.children"
                :key="navigationChild.id"
                class="leading-4"
                :class="navigationElement.customFields?.custom_menu_class ? 'w-full' : ''"
              >
                <NuxtLink
                  :target="
                    navigationChild.externalLink || navigationChild.linkNewTab
                      ? '_blank'
                      : ''
                  "
                  :to="formatLink(getCategoryRoute(navigationChild))"
                  class="text-light-grey text-xs break-words"
                >
                  {{ getTranslatedProperty(navigationChild, "name") }}
                </NuxtLink>
              </li>
            </ul>
          </template>
        </div>

        <div class="payment-method w-full md:w-1/3 sm:w-1/3 xl:w-auto mt-7 xl:mt-0 order-6 lg:order-4">
          <h6 class="text-white text-sm font-bold leading-6 mb-2">
            {{ $t("footer.headline.payment") }}
          </h6>
          <ul class="flex flex-wrap gap-2 sm:gap-1 w-full sm:w-50">
            <template v-for="paymentMethod in payments" :key="paymentMethod.id">
              <li
                v-if="paymentMethod.media"
                class="h-8 w-20 sm:w-15 flex justify-start items-center"
              >
                <NuxtImg
                  :src="paymentMethod.media.url"
                  :alt="paymentMethod.translated.name"
                  loading="lazy"
                  width="2"
                  height="2"
                  class="w-12 h-auto max-w-[100%]"
                />
              </li>
            </template>
          </ul>
        </div>

        <div class="delivery text-white w-1/2 md:w-1/3 xl:w-auto mt-7 xl:mt-0 order-5 lg:order-5">
          <h6 class="text-white text-sm font-bold leading-6 mb-2">
            {{ $t("footer.headline.delivery") }}
          </h6>
          <ul class="space-y-1">
            <template
              v-for="shippingMethod in shippingMethods"
              :key="shippingMethod.id"
            >
              <li v-if="shippingMethod.media">
                <NuxtImg
                  :src="shippingMethod.media.url"
                  :alt="shippingMethod.translated.name"
                  loading="lazy"
                  width="2"
                  height="2"
                  class="w-[65px] h-auto max-w-[100%]"
                />
              </li>
            </template>
          </ul>


          
        </div>

        <div class="follow-us w-1/3 xl:w-auto mt-7 xl:mt-0 order-4 lg:order-6">
          <h6 class="text-white text-sm font-bold leading-6 mb-2">
            {{ $t("footer.headline.followUs") }}
          </h6>
          <div class="grid grid-cols-4 md:grid-cols-3 gap-3 w-35 md:w-24">
            <a
              href="https://www.instagram.com/kisag_official/"
              target="_blank"
              class="w-5 h-5 flex items-center justify-center bg-white text-dark-bg rounded-full"
            >
              <img
                src="assets/images/instagram.svg"
                alt="Instagram"
                class="w-5 h-5"
                loading="lazy"
                height="2"
                width="2"
              />
            </a>
            <a
              href="https://www.tiktok.com/@kisag_official"
              target="_blank"
              class="w-5 h-5 flex items-center justify-center bg-white text-dark-bg rounded-full"
            >
              <img
                src="assets/images/tik-tok.svg"
                alt="Tiktok"
                class="w-5 h-5"
                loading="lazy"
                height="2"
                width="2"
              />
            </a>
            <a
              href="https://www.facebook.com/KisagSwitzerland/"
              target="_blank"
              class="w-5 h-5 flex items-center justify-center bg-white text-dark-bg rounded-full"
            >
              <img
                src="assets/images/facebook.svg"
                alt="Facebook"
                class="w-5 h-5"
                loading="lazy"
                height="2"
                width="2"
              />
            </a>
            <a
              href="https://www.youtube.com/@KisagAG"
              target="_blank"
              class="w-5 h-5 flex items-center justify-center bg-white text-dark-bg rounded-full"
            >
              <img
                src="assets/images/youtube.svg"
                alt="YouTube"
                class="w-5 h-5"
                loading="lazy"
                height="2"
                width="2"
              />
            </a>
            <a
              href="https://www.pinterest.de/Kisag_AG/"
              target="_blank"
              class="w-5 h-5 flex items-center justify-center bg-white text-dark-bg rounded-full"
            >
              <img
                src="assets/images/pintrest.svg"
                alt="Pinterest"
                class="w-5 h-5"
                loading="lazy"
                height="2"
                width="2"
              />
            </a>
            <a
              href="https://ch.linkedin.com/company/kisag-ag?trk=public_profile_topcard_current_company"
              target="_blank"
              class="w-5 h-5 flex items-center justify-center bg-white text-dark-bg rounded-full"
            >
              <img
                src="assets/images/linkedin.svg"
                alt="LinkedIn"
                class="w-5 h-5"
                loading="lazy"
                height="2"
                width="2"
              />
            </a>
          </div>
        </div>


        <div class="certificate-logos text-white w-full md:w-1/3 xl:w-auto mt-7 flex md:block xl:mt-0 order-6 lg:order-7">
          <div class="flex-1">
            <h6 class="text-white text-sm font-bold leading-6 mb-3 mt-0">
              {{ $t("footer.headline.certification") }}
            </h6>
            <ul class="space-y-1">
                <li>
                  <a
                  href="https://cp.kisag.ch/media/54/e1/2d/1757508187/CHZH1303908_CertificateFSC.pdf?ts=1757508492"
                  target="_blank"
                  >
                    <img
                      src="assets/images/fsc.svg"
                      alt="Instagram"
                      class="w-auto h-[50px]"
                      loading="lazy"
                      height="50"
                      width="40"
                    />
                  </a>
                </li>
            </ul>
          </div>


            <div  class="flex-1">
              <h6 class="text-white text-sm font-bold leading-6 mb-3 mt-5">
                {{ $t("footer.headline.shopSafely") }}
              </h6>
              <ul class="flex flex-wrap gap-2 sm:gap-1 w-full sm:w-50">
                <li 
                  class="h-8 w-20 sm:w-15 flex justify-start items-center"
                >
                <img
                  src="assets/images/buysafely.png"
                  alt="Instagram"
                  class="w-10 h-10"
                  loading="lazy"
                  height="40"
                  width="40"
                />
                </li>
              </ul>
            </div>
        </div>
      </div>
    </footer>
     <!-- Back to Top Button -->
     <button 
      v-if="showButton"
      @click="scrollToTop" 
      class="fixed bottom-5 right-5 md:bottom-10 md:right-10 bg-red-bg text-dark-bg p-3 rounded-full shadow-sm  hover:bg-black transition z-40"
    >
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" class="w-[20px] h-[20px] md:w-[30px] md:h-[30px]">
      <path d="M11.9985 3.99997L11.6445 3.64597L11.9985 3.29297L12.3525 3.64597L11.9985 3.99997ZM12.4985 19C12.4985 19.1326 12.4459 19.2598 12.3521 19.3535C12.2583 19.4473 12.1311 19.5 11.9985 19.5C11.8659 19.5 11.7387 19.4473 11.645 19.3535C11.5512 19.2598 11.4985 19.1326 11.4985 19H12.4985ZM5.64453 9.64597L11.6445 3.64597L12.3525 4.35397L6.35253 10.354L5.64453 9.64597ZM12.3525 3.64597L18.3525 9.64597L17.6445 10.354L11.6445 4.35397L12.3525 3.64597ZM12.4985 3.99997V19H11.4985V3.99997H12.4985Z" fill="white"/>
    </svg>
    </button>
  </div>
</template>
